import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../lib/supabase.js'

// Client-side cooldown for the manual "Generate now" button.
// Prevents accidental double-clicks (or rage-clicks) from spending
// extra Anthropic tokens. The server doesn't enforce this — the
// hook is the single point where humans interact with this fn,
// so a 6-second client guard is sufficient.
const GENERATE_NOW_COOLDOWN_MS = 6000

// Drives the Marketing > Drafts screen + the Marketing settings modal.
//
// We fetch the flat list of marketing_drafts ordered newest-first
// (RLS auto-scopes to the user's restaurant) plus the single
// marketing_settings row for that restaurant. Settings can be
// null — meaning the customer hasn't enabled automation yet, the
// settings UI surfaces the "Enable" CTA in that case.
//
// Mutators:
//   approveDraft(id)            mark a draft approved + stamp approved_at
//   rejectDraft(id, note?)      mark rejected + optional note for next-cycle hint
//   editDraft(id, patch)        update caption / hashtags / hook in place
//   markPosted(id)              after the customer pastes it into IG/TikTok
//   saveSettings(patch)         upsert the settings row (creates if missing)
//
// Pattern note: like usePromotionsData we fully refetch after every
// mutation rather than doing optimistic updates. Counts are derived
// in JS so the UI reflects status changes immediately even though
// the round-trip happens.
export function useMarketingDrafts() {
  const [drafts,   setDrafts]   = useState([])
  const [settings, setSettings] = useState(null)
  const [loading,  setLoading]  = useState(true)
  const [error,    setError]    = useState(null)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [draftsRes, settingsRes] = await Promise.all([
        supabase
          .from('marketing_drafts')
          .select(`
            id, platform, post_type, hook, caption, hashtags,
            shot_list, slides, status, generated_for_week,
            usage_input_tokens, usage_output_tokens,
            generated_at, approved_at, rejected_at, posted_at, rejection_note
          `)
          .order('generated_at', { ascending: false }),
        supabase
          .from('marketing_settings')
          .select(`
            restaurant_id, enabled, cadence, generation_day,
            generation_hour, tone_preference, platforms,
            voice_notes, drafts_per_cycle, last_generated_at,
            created_at, updated_at
          `)
          .maybeSingle(),
      ])
      if (draftsRes.error)   throw draftsRes.error
      // No-row is fine for settings — surface as null so the UI can
      // show the empty state.
      if (settingsRes.error && settingsRes.error.code !== 'PGRST116') throw settingsRes.error
      setDrafts(draftsRes.data ?? [])
      setSettings(settingsRes.data ?? null)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useMarketingDrafts]', e)
      setError(e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  // Group drafts by status — drives the inbox tabs.
  const grouped = useMemo(() => {
    const buckets = { draft: [], approved: [], rejected: [], posted: [] }
    for (const d of drafts) {
      if (buckets[d.status]) buckets[d.status].push(d)
    }
    return buckets
  }, [drafts])

  const counts = useMemo(() => ({
    draft:    grouped.draft.length,
    approved: grouped.approved.length,
    rejected: grouped.rejected.length,
    posted:   grouped.posted.length,
  }), [grouped])

  // ---------- mutations ----------

  const approveDraft = useCallback(async (id) => {
    const { error: e } = await supabase
      .from('marketing_drafts')
      .update({ status: 'approved', approved_at: new Date().toISOString() })
      .eq('id', id)
    if (e) throw e
    // Fire-and-forget: notify the operator if the customer is on the
    // managed-posting add-on. The edge function checks the
    // subscription flag itself and no-ops for self-serve customers.
    // We don't await — even if Postmark is down, the approval has
    // already landed and the operator queue page will see it.
    supabase.functions.invoke('notify-managed-post-approved', {
      body: { draft_id: id },
    }).catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('[useMarketingDrafts] notify failed (non-fatal)', err)
    })
    await fetchAll()
  }, [fetchAll])

  const rejectDraft = useCallback(async (id, note = null) => {
    const { error: e } = await supabase
      .from('marketing_drafts')
      .update({
        status: 'rejected',
        rejected_at: new Date().toISOString(),
        rejection_note: note,
      })
      .eq('id', id)
    if (e) throw e
    await fetchAll()
  }, [fetchAll])

  const editDraft = useCallback(async (id, patch) => {
    // Whitelist editable fields so a stray patch can't change ids
    // or status server-side. Server RLS would reject anyway, but
    // catching it client-side gives a cleaner error.
    const allowed = {}
    for (const k of ['hook','caption','hashtags','shot_list','slides']) {
      if (k in patch) allowed[k] = patch[k]
    }
    const { error: e } = await supabase
      .from('marketing_drafts')
      .update(allowed)
      .eq('id', id)
    if (e) throw e
    await fetchAll()
  }, [fetchAll])

  const markPosted = useCallback(async (id) => {
    const { error: e } = await supabase
      .from('marketing_drafts')
      .update({ status: 'posted', posted_at: new Date().toISOString() })
      .eq('id', id)
    if (e) throw e
    await fetchAll()
  }, [fetchAll])

  // Upsert. Settings is keyed on restaurant_id, the trigger fills
  // it in if we omit it.
  const saveSettings = useCallback(async (patch) => {
    const row = { ...patch }
    // Drop nullable empties so triggers and defaults can do their job.
    if (row.voice_notes === '') row.voice_notes = null
    const { error: e } = await supabase
      .from('marketing_settings')
      .upsert(row, { onConflict: 'restaurant_id' })
    if (e) throw e
    await fetchAll()
  }, [fetchAll])

  // Manual on-demand draft generation. Calls the same edge function
  // the cron uses, but in single-restaurant mode that bypasses the
  // time-of-day check.
  //
  // restaurantId  required — caller passes the active restaurant
  // force         optional — when true, wipes the current week's
  //               'draft' rows first so a fresh batch is generated
  //               (approved/posted rows are never touched)
  //
  // Returns the count of drafts generated. Throws on edge fn errors.
  // Throws "rate_limited" if called within the cooldown window of
  // the previous call, to protect Anthropic costs.
  const lastGenerateAtRef = useRef(0)
  const generateNow = useCallback(async ({ restaurantId, force = false }) => {
    if (!restaurantId) throw new Error('restaurantId is required')
    const now = Date.now()
    const since = now - lastGenerateAtRef.current
    if (since < GENERATE_NOW_COOLDOWN_MS) {
      const wait = Math.ceil((GENERATE_NOW_COOLDOWN_MS - since) / 1000)
      throw new Error(`Please wait ${wait}s before generating again.`)
    }
    lastGenerateAtRef.current = now

    const { data: { session } } = await supabase.auth.getSession()
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
    const anonKey     = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY
                     || import.meta.env.VITE_SUPABASE_ANON_KEY
    const token = session?.access_token || anonKey
    const resp = await fetch(`${supabaseUrl}/functions/v1/generate-marketing-drafts`, {
      method: 'POST',
      headers: {
        'content-type':  'application/json',
        'authorization': `Bearer ${token}`,
        'apikey':        anonKey,
      },
      body: JSON.stringify({ restaurant_id: restaurantId, force }),
    })
    const body = await resp.json().catch(() => ({}))
    if (!resp.ok || body?.error) {
      // Surface the edge function's specific error code in a
      // human-readable form. The UI shows this directly.
      const msg = ({
        settings_not_found:    'Enable automation in Settings first.',
        restaurant_not_found:  'Restaurant info missing — refresh and try again.',
        no_platforms_selected: 'Pick at least one platform in Settings.',
      })[body?.error] || body?.error || `generate-marketing-drafts failed (${resp.status})`
      // On error, reset the cooldown timestamp so the user can retry
      // immediately after fixing the underlying issue.
      lastGenerateAtRef.current = 0
      throw new Error(msg)
    }
    await fetchAll()
    return body?.generated ?? 0
  }, [fetchAll])

  return {
    drafts,
    grouped,
    counts,
    settings,
    loading,
    error,
    refresh: fetchAll,
    approveDraft,
    rejectDraft,
    editDraft,
    markPosted,
    saveSettings,
    generateNow,
  }
}
